/**
 * The final report's standard text and equipment list (MB, decision 885; docs/specs/project-reporting/final-report.md
 * §4). Data processors, managers and tenant admins write them in Teslyn Cloud: once for the company, and for a project
 * that needs its own, the project's own text, which then replaces the company's for that project. Teslyn Field reads
 * them (synced, read only) and the final report prints them: the paragraphs of "Specifications" after the survey
 * specifications, the equipment list and the paragraphs of "Survey instrumentation" after the aircraft pages.
 *
 * Nothing is printed until someone writes it (decision 885: the final report leaves out the previous reporting
 * program's standard text until MB sends a current example, decision 886). Thomson Airborne may start from that
 * program's own wording (`previousProgramWording`, its final report script `pntrpfn.scp`), never printed unless saved.
 * The values that program filled in from the job (thorium limits, diurnal limits) are left as blanks, `[fill in: …]`,
 * which Teslyn Cloud and Teslyn Field point out until they are replaced.
 */
import type { Uuid } from './model.js';

/** Where a paragraph prints. */
export type StandardTextPart = 'specifications' | 'instrumentation';

export const STANDARD_TEXT_PARTS: readonly StandardTextPart[] = ['specifications', 'instrumentation'];

/** The part's name, as the report and the editor show it. */
export const STANDARD_TEXT_PART_LABEL: Readonly<Record<StandardTextPart, string>> = {
  specifications: 'Specifications (after the survey specifications)',
  instrumentation: 'Survey instrumentation (after the aircraft)',
};

/** One standard paragraph: a heading and its text (paragraphs split by a blank line; "- " starts a bullet). */
export interface StandardSection {
  readonly heading: string;
  readonly text: string;
  readonly part: StandardTextPart;
}

/** One instrument of the equipment list: what it is, its make and model, and its serial number where relevant. */
export interface EquipmentItem {
  readonly instrument: string;
  readonly model: string;
  readonly serial: string;
}

/** The standard text of the company, or of one project. */
export interface FinalReportText {
  readonly sections: readonly StandardSection[];
  readonly equipment: readonly EquipmentItem[];
}

export const EMPTY_FINAL_REPORT_TEXT: FinalReportText = { sections: [], equipment: [] };

/** A `final_report_text` row as Teslyn Cloud keeps it and Teslyn Field receives it (read only). */
export interface FinalReportTextRow {
  readonly id: Uuid;
  readonly tenant_id: Uuid;
  /** Null: the company's text. Otherwise the project whose own text this is. */
  readonly project_id: Uuid | null;
  readonly sections: readonly StandardSection[];
  readonly equipment: readonly EquipmentItem[];
  /** Who saved it last, from the staff register (stamped by Teslyn Cloud). */
  readonly changed_by_name: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly updated_by: Uuid | null;
  readonly deleted_at: string | null;
}

export const STANDARD_SECTIONS_MAX = 40;
export const STANDARD_HEADING_MAX = 120;
export const STANDARD_TEXT_MAX = 8000;
export const EQUIPMENT_MAX = 60;
export const EQUIPMENT_NAME_MAX = 120;
export const EQUIPMENT_SERIAL_MAX = 60;

/** Who writes them (decision 885: data processors; managers and tenant admins too, as they print the final report). */
export const FINAL_REPORT_TEXT_ROLES: readonly string[] = ['tenant_admin', 'manager', 'data_processor'];

export function canEditFinalReportText(roles: readonly string[] | null | undefined): boolean {
  return (roles ?? []).some((r) => FINAL_REPORT_TEXT_ROLES.includes(r));
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * What is wrong with a standard text, in words; empty when it may be saved. The same limits are the database's
 * (`final_report_text_ok`, 20261030120000).
 */
export function validateFinalReportText(value: unknown): string[] {
  if (!isRecord(value) || !Array.isArray(value.sections) || !Array.isArray(value.equipment))
    return ['The standard text is not a list of paragraphs and an equipment list.'];
  const out: string[] = [];
  const { sections, equipment } = value;
  if (sections.length > STANDARD_SECTIONS_MAX)
    out.push(`Keep to ${STANDARD_SECTIONS_MAX} paragraphs or fewer.`);
  sections.forEach((s: unknown, i) => {
    const n = i + 1;
    if (!isRecord(s)) {
      out.push(`Paragraph ${n} is not a heading and a text.`);
      return;
    }
    const heading = typeof s.heading === 'string' ? s.heading.trim() : '';
    const text = typeof s.text === 'string' ? s.text.trim() : '';
    if (heading === '') out.push(`Paragraph ${n} needs a heading.`);
    else if (heading.length > STANDARD_HEADING_MAX)
      out.push(`Paragraph ${n}'s heading is longer than ${STANDARD_HEADING_MAX} characters.`);
    if (text === '') out.push(`Paragraph ${n} (${heading || 'no heading'}) has no text.`);
    else if (text.length > STANDARD_TEXT_MAX)
      out.push(`Paragraph ${n} (${heading}) is longer than ${STANDARD_TEXT_MAX} characters.`);
    if (!STANDARD_TEXT_PARTS.includes(s.part as StandardTextPart))
      out.push(`Choose where paragraph ${n} (${heading || 'no heading'}) prints.`);
  });
  if (equipment.length > EQUIPMENT_MAX) out.push(`Keep to ${EQUIPMENT_MAX} instruments or fewer.`);
  equipment.forEach((e: unknown, i) => {
    const n = i + 1;
    if (!isRecord(e)) {
      out.push(`Instrument ${n} is not an instrument, model and serial number.`);
      return;
    }
    const str = (k: string): string | null => (typeof e[k] === 'string' ? (e[k] as string) : null);
    const instrument = str('instrument')?.trim() ?? '';
    const model = str('model');
    const serial = str('serial');
    if (instrument === '') out.push(`Instrument ${n} needs its name, for example Magnetometer.`);
    else if (instrument.length > EQUIPMENT_NAME_MAX)
      out.push(`Instrument ${n}'s name is longer than ${EQUIPMENT_NAME_MAX} characters.`);
    if (model === null || serial === null)
      out.push(`Instrument ${n} (${instrument || 'no name'}) needs a model and a serial number, even empty.`);
    else {
      if (model.trim().length > EQUIPMENT_NAME_MAX)
        out.push(`Instrument ${n}'s model is longer than ${EQUIPMENT_NAME_MAX} characters.`);
      if (serial.trim().length > EQUIPMENT_SERIAL_MAX)
        out.push(`Instrument ${n}'s serial number is longer than ${EQUIPMENT_SERIAL_MAX} characters.`);
    }
  });
  return out;
}

/** The text as it is saved: every value trimmed, nothing else changed. */
export function tidyFinalReportText(t: FinalReportText): FinalReportText {
  return {
    sections: t.sections.map((s) => ({
      heading: s.heading.trim(),
      text: s.text.replace(/\r\n?/g, '\n').trim(),
      part: s.part,
    })),
    equipment: t.equipment.map((e) => ({
      instrument: e.instrument.trim(),
      model: e.model.trim(),
      serial: e.serial.trim(),
    })),
  };
}

/** Whether there is anything to print. */
export function hasFinalReportText(t: FinalReportText): boolean {
  return t.sections.length > 0 || t.equipment.length > 0;
}

/** Whose text the final report of a project prints. */
export type FinalReportTextSource = 'project' | 'company' | 'none';

export interface ResolvedFinalReportText {
  readonly source: FinalReportTextSource;
  readonly text: FinalReportText;
  /** The row printed; null with nothing written. */
  readonly row: FinalReportTextRow | null;
}

/**
 * The text a project's final report prints: the project's own when it has one, else the company's, else none.
 * Removed rows (deleted_at) are never used.
 */
export function finalReportTextFor(
  rows: readonly FinalReportTextRow[],
  projectId: Uuid,
): ResolvedFinalReportText {
  const live = rows.filter((r) => r.deleted_at === null);
  const pick = (want: Uuid | null): FinalReportTextRow | null =>
    live
      .filter((r) => r.project_id === want)
      .sort((a, b) => (a.updated_at < b.updated_at ? 1 : a.updated_at > b.updated_at ? -1 : 0))[0] ?? null;
  const own = pick(projectId);
  if (own) return { source: 'project', text: own, row: own };
  const company = pick(null);
  if (company) return { source: 'company', text: company, row: company };
  return { source: 'none', text: EMPTY_FINAL_REPORT_TEXT, row: null };
}

/** A blank left to fill in: `[fill in: thorium limit of the calibrations]`. */
const BLANK = /\[fill in:[^\]]*\]/g;

/** Every blank still to fill in, in order, with where it is ("Base station magnetometer", "Instrument 7, model"). */
export function textBlanks(t: FinalReportText): { readonly where: string; readonly blank: string }[] {
  const out: { where: string; blank: string }[] = [];
  const scan = (where: string, s: string): void => {
    for (const m of s.matchAll(BLANK)) out.push({ where, blank: m[0] });
  };
  t.sections.forEach((s, i) => {
    scan(s.heading || `Paragraph ${i + 1}`, s.heading);
    scan(s.heading || `Paragraph ${i + 1}`, s.text);
  });
  t.equipment.forEach((e, i) => {
    const name = e.instrument || `Instrument ${i + 1}`;
    scan(name, e.instrument);
    scan(`${name}, model`, e.model);
    scan(`${name}, serial number`, e.serial);
  });
  return out;
}

/** One printed block of a paragraph's text: lines of one paragraph, or the items of a bullet list. */
export interface TextBlock {
  readonly bullets: boolean;
  readonly lines: readonly string[];
}

/**
 * A paragraph's text as the report prints it: blocks split by blank lines; a block whose every line starts with "- "
 * (or "• ") is a bullet list; other lines keep their line breaks.
 */
export function textBlocks(text: string): TextBlock[] {
  return text
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .map((b) =>
      b
        .split('\n')
        .map((l) => l.trim())
        .filter((l) => l !== ''),
    )
    .filter((lines) => lines.length > 0)
    .map((lines) =>
      lines.every((l) => /^[-•]\s+/.test(l))
        ? { bullets: true, lines: lines.map((l) => l.replace(/^[-•]\s+/, '')) }
        : { bullets: false, lines },
    );
}

/** The standard text as the final report manifest carries it (`FinalReportSection.standard_text`). */
export interface StandardTextManifest {
  readonly specifications: readonly { readonly heading: string; readonly blocks: readonly TextBlock[] }[];
  readonly instrumentation: readonly { readonly heading: string; readonly blocks: readonly TextBlock[] }[];
  /** Instrument, Model and, when any instrument has one, Serial number. */
  readonly equipment: { readonly serials: boolean; readonly rows: readonly (readonly string[])[] };
}

export function standardTextManifest(t: FinalReportText): StandardTextManifest {
  const of = (part: StandardTextPart) =>
    t.sections
      .filter((s) => s.part === part)
      .map((s) => ({ heading: s.heading.trim(), blocks: textBlocks(s.text) }));
  const serials = t.equipment.some((e) => e.serial.trim() !== '');
  return {
    specifications: of('specifications'),
    instrumentation: of('instrumentation'),
    equipment: {
      serials,
      rows: t.equipment.map((e) =>
        serials
          ? [e.instrument.trim(), e.model.trim(), e.serial.trim()]
          : [e.instrument.trim(), e.model.trim()],
      ),
    },
  };
}

// ---------------------------------------------------------------- the previous reporting program's wording

/** The organisations offered the previous reporting program's wording: it is Thomson Airborne's own text. */
export const PREVIOUS_WORDING_TENANT_SLUGS: readonly string[] = ['thomson-airborne'];

export function previousWordingOffered(tenantSlug: string | null | undefined): boolean {
  return tenantSlug != null && PREVIOUS_WORDING_TENANT_SLUGS.includes(tenantSlug.trim().toLowerCase());
}

/** The two versions of that program's final report script: magnetics only, or magnetics and radiometrics. */
export type PreviousWordingSurvey = 'magnetics' | 'magnetics_radiometrics';

export const PREVIOUS_WORDING_SURVEY_LABEL: Readonly<Record<PreviousWordingSurvey, string>> = {
  magnetics: 'Magnetics',
  magnetics_radiometrics: 'Magnetics and radiometrics',
};

const FREQUENCY_DAILY =
  'Frequency: Before the first flight and after the last flight of each day when survey operations were conducted.';

/**
 * The previous reporting program's final report wording (its script `pntrpfn.scp`, Part 1 and Part 3; a decoded copy
 * is `reference/tjr report scripts/pntrpfn.scp.txt`), as a starting text. Changed from the script only where Teslyn
 * must: Australian spelling (meter, liters and analog to metre, litres and analogue), a label and its value on one line ("Frequency: …"),
 * the "Type:" lines moved into the equipment list, and the values the program filled in from the job left as blanks.
 * The script's `if Rad` parts are kept for "Magnetics and radiometrics" only.
 */
export function previousProgramWording(survey: PreviousWordingSurvey): FinalReportText {
  const rad = survey === 'magnetics_radiometrics';
  const spec = (heading: string, paragraphs: readonly string[]): StandardSection => ({
    heading,
    text: paragraphs.join('\n\n'),
    part: 'specifications',
  });
  const inst = (heading: string, paragraphs: readonly string[]): StandardSection => ({
    heading,
    text: paragraphs.join('\n\n'),
    part: 'instrumentation',
  });
  const compensation = [
    'Compensation flights for all aircraft were carried out to determine how aircraft manoeuvring effects affected the magnetic data. These aircraft induced effects are then removed from the collected magnetic data during data processing.',
    'Frequency: Before the commencement of the survey project and after each scheduled maintenance operation.',
  ];
  const sections: StandardSection[] = [
    ...(rad
      ? [
          spec('Calibration range', [
            'The radiometric systems for each aircraft were calibrated using the Geoscience Australia calibration range in Carnamah WA to determine the ground concentration coefficients for the radiometric systems.',
            'Cosmic stacks were flown over water to determine the aircraft and cosmic coefficients.',
            'Height attenuation coefficients were determined from IAEA recommended attenuation coefficients.',
          ]),
        ]
      : []),
    spec(
      'In field calibration',
      rad
        ? [
            'a) Spectrometer',
            'The gamma ray spectrometer response was verified by exposing the system to thorium test samples for a time sufficient to accumulate 10,000 counts.',
            FREQUENCY_DAILY,
            'All background corrected counts fell within [fill in: thorium limit of the calibrations] of the mean over the survey period.',
            'Results of thorium test sample measurements can be found in Appendix B and Appendix C.',
            'b) Test Line',
            'Test lines are flown at the specified survey height to verify magnetometer, spectrometer and barometric altimeter baselines.',
            'Length: 10km\nDirection: Bi-Directional',
            FREQUENCY_DAILY,
            'The test line thorium counts fell within [fill in: thorium limit of the test lines] of the mean over the survey period.',
            'Results of test line thorium counts can be found in Appendix B and Appendix C',
            'c) Compensation Flight',
            ...compensation,
          ]
        : ['Compensation Flight', ...compensation],
    ),
    spec('In field verification and processing', [
      'Thomson Airborne conducts stringent real time data validity checks.',
      'The following products were generated on site utilising ChrisDBF and Geosoft database programs as well as Thomson Airborne proprietary software:',
      [
        '- Flight path plots, to demonstrate quality of navigation',
        '- Magnetic stacked profiles, to demonstrate character of magnetic data',
        '- Statistical summary of line data',
        '- Magnetometer base station plots',
        '- Progressive image presentation of magnetic and topographic data',
        '- Daily plots of aircraft parking locations to verify GPS position',
      ].join('\n'),
    ]),
    spec('Navigation and positioning', [
      'Navigation was provided using a mobile Novatel OEMV-1 VBS receiver. This equipment provides flight guidance to the pilot as well as flight path information which was recorded for subsequent processing.',
      'Differential GPS data was obtained in real time using static GPS data obtained from the Omnistar wide area GPS service.',
      'Position relative to the survey line was displayed to the pilot by an accurate and effective system proprietary to Thomson Airborne.',
      'Under normal circumstances differential GPS is expected to yield positional accuracies in the order of 5 metres RMS or better.',
    ]),
    inst('Magnetometer', [
      'The Geometrics G822A Magnetometer is a highly sensitive unit incorporating an optically pumped sensor. The constant harmonic frequency from the sensor is proportional to the surrounding scalar magnetic field. This frequency is resolved by the Counter / Processor which provides the magnetic field to a nominal accuracy of 0.001nT with a data capture rate of 20 times per second both in analogue and digital formats.',
      'The sensor and pre-amp are mounted in a stinger assembly which may be attached to the front or rear of the survey aircraft.',
    ]),
    inst('Radar altimeter', [
      'This unit is a high resolution, short pulse ratio altitude system designed for automatic continuous operation over wide variations of terrain and weather conditions, target reflectivity, and aircraft altitude. It provides an accurate terrain clearance indication ranging from 0 to 650m (0 to 2,000ft).',
    ]),
    inst('Barometric altimeter', [
      'This type of pressure transducer operates over a range of 600 to 1100 mB and its resolution is limited only by system noise. The sensor is referenced to the height given by the GPS.',
    ]),
    inst('Data acquisition system', [
      'The GeOZ system manages the data acquisition and saves the data to removable Flash Cards.',
      'It provides guidance and real time error diagnostics for the pilot.',
      'Acquired data is transferred to a field computer on completion of the flight for both verification and archiving prior to being shipped to the processing centre.',
    ]),
    inst('Navigation equipment', [
      'This equipment is a 12 channel parallel tracking receiver capable of providing sub-metre resolution at 5Hz and is integrated with the GeOZ-ZDAS acquisition unit.',
    ]),
    ...(rad
      ? [
          inst('Gamma ray spectrometer system', [
            'These units deliver high-resolution spectral information from 0.33 MeV to 3.0 MeV including the five primary regions of interest; Total Count, Potassium, Uranium, Thorium and Cosmic.',
            'The Gamma Ray Spectrometer is interfaced to a NaI (Tl) crystal detector pack with a total volume of 33 litres (2048 cubic inches). These detector packs embody the latest techniques whereby the elimination of dead time in the counting process yields up to 30% more counts over other commercial systems.',
            'Superior calibration facilities included the visual real time monitoring of full spectrum data and in flight monitoring of gain drift relative to the selected isotope window maintain long-term data quality.',
            'Enhancement of the spectrometer data is achieved by noise reduction techniques (NASVD or MNF), followed by dead time correction, energy calibration, cosmic/aircraft background correction and atmospheric radon removal all applied to the 256 channel data. Spectral stripping, height correction and conversion to radio-element concentrations are then applied prior to gridding and micro-levelling.',
            'The gamma ray spectrometer response was verified by exposing the system to thorium test samples for a time sufficient to accumulate 10,000 counts.',
          ]),
        ]
      : []),
    inst('Base station magnetometer', [
      'Two units are used in tandem for diurnal monitoring. These units run continuously during the survey periods and record the data in digital format.',
      'Base station magnetometer instruments record data to a sensitivity of 0.1nT every 6 seconds.',
      'During data acquisition, if the non-linear diurnal variation was greater than [fill in: non-linear limit] nT in [fill in: non-linear window] minutes, or the deviation from a straight line chord of length [fill in: chord length] minutes exceeded [fill in: chord limit] nT, the line was reflown.',
    ]),
  ];
  const equipment: EquipmentItem[] = [
    { instrument: 'Magnetometer', model: 'Geometrics G822A Magnetometer', serial: '' },
    { instrument: 'Radar altimeter', model: 'King KRA405B Radar Altimeter', serial: '' },
    { instrument: 'Barometric altimeter', model: 'Setra 276 Pressure Transducer', serial: '' },
    { instrument: 'Data acquisition system', model: 'GeOZ-ZDAS Digital Data Acquisition System', serial: '' },
    { instrument: 'Navigation equipment', model: 'Novatel OEMV-1VBS GPS Receiver', serial: '' },
    ...(rad
      ? [
          {
            instrument: 'Gamma ray spectrometer system',
            model: 'Radiations Solutions Inc. RS 400 Spectrometer',
            serial: '',
          },
        ]
      : []),
    {
      instrument: 'Base station magnetometer',
      model: '[fill in: base station magnetometer, G856 or G823]',
      serial: '',
    },
  ];
  return { sections, equipment };
}
